import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getOAuthAuthorizeUrl } from '../api/auth';

/** Google multicolor "G" logo (20x20) */
function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 001 12c0 1.94.46 3.77 1.18 5.43l3.66-2.84z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

// /** Microsoft 4-square logo (20x20) */
// function MicrosoftIcon() {
//   return (
//     <svg width="20" height="20" viewBox="0 0 21 21" fill="none" aria-hidden="true">
//       <rect x="1" y="1" width="9" height="9" fill="#F25022" />
//       <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
//       <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
//       <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
//     </svg>
//   );
// }

/** GitHub octocat silhouette (20x20) */
function GitHubIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
    </svg>
  );
}

const PROVIDERS = [
  {
    id: 'google',
    name: 'Google',
    icon: <GoogleIcon />,
  },
  // {
  //   id: 'microsoft',
  //   name: 'Microsoft',
  //   icon: <MicrosoftIcon />,
  // },
  {
    id: 'github',
    name: 'GitHub',
    icon: <GitHubIcon />,
  },
] as const;

interface SocialLoginButtonsProps {
  mode: 'login' | 'register';
}

/**
 * Renders OAuth social login buttons for Google, Microsoft, and GitHub.
 * Used on both Login and Register pages.
 *
 * Flow: requests an authorize URL from the backend, stores the provider
 * name in localStorage, then performs a full-page redirect.
 */
export default function SocialLoginButtons({ mode }: SocialLoginButtonsProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleOAuth = async (provider: string) => {
    setLoading(provider);
    setError(null);
    try {
      // Store provider so the callback page knows which provider completed auth
      localStorage.setItem('cloudmaster_oauth_provider', provider);
      const data = await getOAuthAuthorizeUrl(provider);
      // Full page redirect to the OAuth provider's consent screen
      window.location.assign(data.authorize_url);
    } catch {
      setLoading(null);
      localStorage.removeItem('cloudmaster_oauth_provider');
      setError(t('auth.social.error'));
    }
  };

  const label = mode === 'login' ? t('auth.social.signInWith') : t('auth.social.signUpWith');

  return (
    <div className="space-y-3">
      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-error" role="alert">
          {error}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        {PROVIDERS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => handleOAuth(p.id)}
            disabled={loading !== null}
            aria-label={`${label} ${p.name}`}
            className={`flex items-center justify-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium
              text-slate-700 transition-colors hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10
              ${loading === p.id ? 'opacity-70 cursor-wait' : ''}
              ${loading !== null && loading !== p.id ? 'opacity-50 cursor-not-allowed' : ''}
              focus:outline-none focus:ring-[3px] focus:ring-blue-600/15 focus:ring-offset-1 focus:ring-offset-transparent`}
          >
            <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
              {p.icon}
            </span>
            {loading === p.id ? t('auth.social.redirecting') : p.name}
          </button>
        ))}
      </div>
    </div>
  );
}
